import { useQuery } from "convex/react";
import { useMemo } from "react";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";

import { useSession } from "./useSession";

/**
 * Résolution de l'org ACTIVE = celle où le user est connecté (org de session
 * WorkOS), et PAS « la première de la liste ».
 *
 * Pourquoi ce module existe (bug multi-org 2026-06-25) :
 *   `accounts.listMine` renvoie TOUTES les memberships du user, triées par
 *   ancienneté (index Convex `by_user`). Historiquement le shell lisait `[0]`
 *   en supposant 1 org par user (cf l'ancien commentaire de `usePermissions`).
 *   Un user multi-org — typiquement un chatter qui a AUSSI fondé sa propre org
 *   avant de rejoindre une agence — voyait alors tout le Convex-scoped (gate de
 *   shift, verrou de conversation, permissions, panneau Personnel) évalué sur
 *   la MAUVAISE org : la plus ancienne (souvent la sienne, vide), pas celle où
 *   il bosse. Symptômes : il chatte sans gate, son verrou est invisible pour
 *   les autres, son Personnel est vide.
 *
 *   Les conversations elles-mêmes viennent du backend Cyrano scopé sur l'org de
 *   SESSION (`/api/auth/me` → `workosOrgId`). On aligne donc le Convex-scoped
 *   sur cette même org de session.
 *
 * Mono-org (cas quasi universel) : l'org de session EST `[0]` → le `find`
 * retombe sur `[0]`, comportement strictement inchangé. Le fallback `[0]`
 * couvre le bootstrap (session pas encore résolue) et le cas — transitoire —
 * où l'org de session n'est pas encore dans `listMine`.
 */

/** Forme minimale attendue d'un account `listMine` pour le matching. */
interface AccountLike {
  _id: Id<"accounts">;
  workosOrgId: string;
}

/**
 * Choisit l'account dont le `workosOrgId` matche l'org de session ; sinon
 * retombe sur le premier. Pure (testable, pas de hook) — réutilisée par les
 * call-sites qui ont déjà chargé `listMine` + la session.
 */
export function pickActiveAccount<T extends AccountLike>(
  accounts: readonly T[] | undefined | null,
  sessionWorkosOrgId: string | null | undefined,
): T | null {
  if (!accounts || accounts.length === 0) return null;
  if (sessionWorkosOrgId) {
    const match = accounts.find((a) => a.workosOrgId === sessionWorkosOrgId);
    if (match) return match;
  }
  return accounts[0] ?? null;
}

/**
 * Hook : l'account actif (org de session) + la liste brute + l'état de
 * chargement. Source unique pour « dans quelle org suis-je en train d'opérer ».
 */
export function useActiveAccount() {
  const { user } = useSession();
  const accounts = useQuery(api.accounts.listMine);
  const account = useMemo(
    () => pickActiveAccount(accounts, user?.workosOrgId),
    [accounts, user?.workosOrgId],
  );
  return {
    account,
    accountId: account?._id ?? null,
    accounts,
    isLoading: accounts === undefined,
  };
}

/** Raccourci : juste l'id de l'account actif (ou null pendant le bootstrap). */
export function useActiveAccountId(): Id<"accounts"> | null {
  return useActiveAccount().accountId;
}
